import { CommunityRole } from "@upsquad/shared";
import {
	BarChart3,
	Briefcase,
	Building2,
	CalendarDays,
	ChevronDown,
	Home,
	KeyRound,
	Layers,
	Link as LinkIcon,
	Megaphone,
	MessageSquare,
	Plus,
	Settings,
	Shield,
	Sparkles,
	Users,
	Video,
	Webhook,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";
import { ADMIN_ROUTES } from "@/src/admin/constants/admin-routes";
import { AI_ROUTES } from "@/src/ai/constants/ai-routes";
import { getIsStaff } from "@/src/auth/store/auth-selectors";
import { env } from "@/src/common/constants/env";
import { buildMainAppUrl } from "@/src/common/lib/subdomain";
import { useAppSelector } from "@/src/common/redux/store";
import { getActiveCommunityId, getActiveMemberRole, getCommunities } from "@/src/community/store/community-selectors";
import { ManageLinksModal } from "@/src/community-link/components/manage-links-modal";
import { useListCommunityLinksQuery } from "@/src/community-link/services/community-link-http.service";
import { EVENT_ROUTES } from "@/src/event/constants/event-routes";
import { GROUP_ROUTES } from "@/src/group/constants/group-routes";
import { useGetGroupJoinRequestsCountQuery } from "@/src/group/services/group-http.service";
import { MEETING_ROUTES } from "@/src/meeting/constants/meeting-routes";
import { MEMBER_ROUTES } from "@/src/member/constants/member-routes";
import { MESSAGE_ROUTES } from "@/src/message/constants/message-routes";
import { getUnreadCount } from "@/src/message/store/message-selectors";
import { MODERATION_ROUTES } from "@/src/moderation/constants/moderation-routes";
import { POST_ROUTES } from "@/src/post/constants/post-routes";
import { STAFF_ROUTES } from "@/src/staff/constants/staff-routes";
import { CommunitySwitcherTrigger } from "./community-switcher";

const isAdminSectionPath = (pathname: string) => pathname.startsWith(ADMIN_ROUTES.ROOT) || pathname === AI_ROUTES.CHAT;

const NAV_ITEMS = [
	{ key: "home", path: POST_ROUTES.FEED, icon: Home, labelKey: "nav.home" },
	{ key: "members", path: MEMBER_ROUTES.LIST, icon: Users, labelKey: "nav.members" },
	{ key: "messages", path: MESSAGE_ROUTES.LIST, icon: MessageSquare, labelKey: "nav.messages" },
	{ key: "meetings", path: MEETING_ROUTES.LIST, icon: Video, labelKey: "nav.meetings" },
	{ key: "events", path: EVENT_ROUTES.LIST, icon: CalendarDays, labelKey: "nav.events" },
] as const;

interface AppSidebarProps {
	onNavigate?: () => void;
}

export function AppSidebar({ onNavigate }: AppSidebarProps) {
	const { t } = useTranslation("shell");
	const communities = useAppSelector(getCommunities);
	const activeCommunityId = useAppSelector(getActiveCommunityId);
	const role = useAppSelector(getActiveMemberRole);
	const unreadCount = useAppSelector(getUnreadCount);
	const isStaff = useAppSelector(getIsStaff);
	const isOwnerOrMod = role === CommunityRole.OWNER || role === CommunityRole.ADMIN || role === CommunityRole.MODERATOR;
	const isOwner = role === CommunityRole.OWNER || role === CommunityRole.ADMIN;
	const { pathname } = useLocation();
	const [adminOpen, setAdminOpen] = useState(() => isAdminSectionPath(pathname));
	const [linksModalOpen, setLinksModalOpen] = useState(false);
	const { data: groupRequestsCountData } = useGetGroupJoinRequestsCountQuery(activeCommunityId ?? "", {
		skip: !activeCommunityId || !isOwner,
		pollingInterval: 60000,
	});
	const pendingGroupRequests = groupRequestsCountData?.pending ?? 0;

	useEffect(() => {
		if (isAdminSectionPath(pathname)) setAdminOpen(true);
	}, [pathname]);

	const { data: linksData } = useListCommunityLinksQuery(activeCommunityId ? { communityId: activeCommunityId } : { communityId: "" }, {
		skip: !activeCommunityId,
	});
	const links = linksData?.links ?? [];

	const openExternal = (url: string) => {
		window.open(url, "_blank", "noopener,noreferrer");
	};

	return (
		<aside className={`flex flex-col bg-white dark:bg-neutral-900 ${onNavigate ? "w-full" : "h-screen w-72"}`}>
			{!onNavigate && (
				<div className="p-4">
					<CommunitySwitcherTrigger communities={communities} />
				</div>
			)}

			<nav className="flex-1 overflow-y-auto min-h-0 px-2 py-4">
				<ul className="space-y-1">
					{NAV_ITEMS.map((item) => (
						<li key={item.key}>
							<NavLink
								to={item.path}
								onClick={onNavigate}
								className={({ isActive }) =>
									`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
										isActive ? "bg-accent-soft-hover text-accent" : "text-foreground/70 hover:bg-accent/10 hover:text-accent/90"
									}`
								}
							>
								<item.icon size={20} />
								<span className="flex-1">{t(item.labelKey)}</span>
								{item.key === "messages" && unreadCount > 0 && (
									<span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold text-white">
										{unreadCount > 99 ? "99+" : unreadCount}
									</span>
								)}
							</NavLink>
						</li>
					))}
					<li>
						<NavLink
							to={GROUP_ROUTES.GROUPS}
							onClick={onNavigate}
							className={({ isActive }) =>
								`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
									isActive ? "bg-accent-soft-hover text-accent" : "text-foreground/70 hover:bg-accent/10 hover:text-accent/90"
								}`
							}
						>
							<Layers size={20} />
							<span className="flex-1">{t("nav.groups")}</span>
						</NavLink>
					</li>
					{isOwnerOrMod && (
						<li>
							<NavLink
								to={MODERATION_ROUTES.ROOT}
								onClick={onNavigate}
								className={({ isActive }) =>
									`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
										isActive ? "bg-accent-soft-hover text-accent" : "text-foreground/70 hover:bg-accent/10 hover:text-accent/90"
									}`
								}
							>
								<Shield size={20} />
								<span>{t("nav.moderation")}</span>
							</NavLink>
						</li>
					)}
					{links.map((link) => (
						<li key={link.id}>
							<button
								type="button"
								onClick={() => openExternal(link.url)}
								className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-foreground/70 transition-colors hover:bg-accent/10 hover:text-accent/90"
							>
								<LinkIcon size={20} />
								<span className="flex-1 truncate">{link.title}</span>
							</button>
						</li>
					))}
					{isOwner && (
						<li>
							<button
								type="button"
								onClick={() => setLinksModalOpen(true)}
								className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-foreground/70 transition-colors hover:bg-accent/10 hover:text-accent/90"
							>
								<Plus size={20} />
								<span>{t("nav.add_links")}</span>
							</button>
						</li>
					)}
				</ul>

				{isStaff && (
					<div className="mt-4 border-t border-default-200 pt-4">
						<div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-foreground/40">{t("nav.staff")}</div>
						<ul className="space-y-1">
							<li>
								<a
									href={buildMainAppUrl(STAFF_ROUTES.COMMUNITIES)}
									target="_blank"
									rel="noopener noreferrer"
									onClick={onNavigate}
									className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-foreground/70 hover:bg-accent/10 hover:text-accent/90"
								>
									<Briefcase size={20} />
									<span>{t("nav.staff_communities")}</span>
								</a>
							</li>
						</ul>
					</div>
				)}

				{isOwner && (
					<div className="mt-4 border-t border-default-200 pt-4">
						<button
							type="button"
							onClick={() => setAdminOpen((prev) => !prev)}
							className={`flex w-full items-center justify-between ${adminOpen ? "rounded-t-lg" : "rounded-lg"} px-3 py-2 text-sm font-medium bg-accent/10 text-accent/90 transition-colors"`}
						>
							<span className="flex items-center gap-3">
								<Settings size={20} />
								{t("nav.admin")}
							</span>
							<ChevronDown size={16} className={`transition-transform ${adminOpen ? "rotate-180" : ""}`} />
						</button>
						{adminOpen && (
							<ul className="pt-1 pb-2 pl-5 space-y-1 bg-accent/10 rounded-b-lg">
								<li>
									<NavLink
										to={ADMIN_ROUTES.ANALYTICS}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<BarChart3 size={16} />
										<span>{t("nav.admin_analytics")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.MEMBERS}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<Users size={16} />
										<span>{t("nav.admin_members")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.GROUPS}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<Layers size={16} />
										<span className="flex-1">{t("nav.admin_groups")}</span>
										{pendingGroupRequests > 0 && (
											<span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold text-accent-foreground">
												<span aria-hidden="true">{pendingGroupRequests > 99 ? "99+" : pendingGroupRequests}</span>
												<span className="sr-only">{t("nav.pending_group_requests", { count: pendingGroupRequests })}</span>
											</span>
										)}
									</NavLink>
								</li>
								<li>
									<NavLink
										to={AI_ROUTES.CHAT}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "bg-accent-soft-hover text-accent" : "text-foreground/70 hover:bg-accent/10 hover:text-accent/90"
											}`
										}
									>
										<Sparkles size={20} />
										<span>{t("nav.ai_insights")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.COMMUNITY}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<Building2 size={16} />
										<span>{t("nav.community")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.SPONSORSHIP}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<Megaphone size={16} />
										<span>{t("nav.sponsorship")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.API_KEYS}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<KeyRound size={16} />
										<span>{t("nav.api_keys")}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to={ADMIN_ROUTES.WEBHOOKS}
										onClick={onNavigate}
										className={({ isActive }) =>
											`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
												isActive ? "text-accent" : "text-foreground/70 hover:text-accent/90"
											}`
										}
									>
										<Webhook size={16} />
										<span>{t("nav.webhooks")}</span>
									</NavLink>
								</li>
							</ul>
						)}
					</div>
				)}
			</nav>

			{!onNavigate && (
				<div className="text-center p-4">
					<p className="text-xs text-foreground/40">{t("powered_by", { app_name: env.APP_NAME })}</p>
				</div>
			)}
			<ManageLinksModal isOpen={linksModalOpen} onClose={() => setLinksModalOpen(false)} />
		</aside>
	);
}
